import {Contact, InvoiceInfo, School, SchoolClass, User, VatNumber} from "models"
import React, {useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import Heading from "components/utils/heading"
import InputValidator from "shared/input-validator"
import isUserA from "components/users/is-a"
import {loading} from "components/layout/loading-blocker"
import memo from "set-state-compare/build/memo"
import ModelPropType from "@kaspernj/api-maker/build/model-prop-type"
import presence from "shared/presence"
import PropTypes from "prop-types"
import useCurrentCountry from "components/utils/use-current-country"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import UtilsButton from "components/utils/button"
import UtilsInput from "components/utils/input"
import {View} from "react-native"

/**
 * @typedef {object} ComponentsPlansSignUpModalStepInvoiceInfoProps
 * @property {unknown} onInvoiceInfoSubmit
 * @property {unknown} resource
 * @property {unknown} school
 */

/**
 * @typedef {object} ComponentsPlansSignUpModalStepInvoiceInfoState
 * @property {unknown} schoolName
 */

/**
 * @extends {ShapeComponent<ComponentsPlansSignUpModalStepInvoiceInfoProps, ComponentsPlansSignUpModalStepInvoiceInfoState>}
 */
class ComponentsPlansSignUpModalStepInvoiceInfo extends ShapeComponent {
  /** @type {ComponentsPlansSignUpModalStepInvoiceInfoState} */
  state = {
    schoolName: undefined
  }

  static propTypes = {
    resource: PropTypes.oneOfType([PropTypes.instanceOf(School), PropTypes.instanceOf(SchoolClass), PropTypes.instanceOf(User)]).isRequired,
    school: ModelPropType.ofModel(School)
      .withLoadedAttributes(["id", "name"])
      .withLoadedAssociation("country")
      .isNotRequired
  }

  setup() {
    const {t} = useI18n({namespace: "js.components.plans.sign_up_modal.step_invoice_info"})
    const currentCountry = useCurrentCountry()
    const {school} = this.p
    let country
    let schoolName

    if (school) {
      if (school?.isAssociationLoaded("country")) {
        country = school.country()
      }

      schoolName = school.name()
    }

    country ||= currentCountry
    schoolName ||= ""

    const formRef = useRef()

    this.formRef = formRef
    this.t = t
    this.country = country
    this.currentUser = useCurrentUser()

    if (this.state.schoolName === undefined) {
      this.state.schoolName = schoolName
    }
  }

  componentDidMount() {
    void this.ensureSchoolDefaultsLoaded()
  }

  render() {
    const {country, formRef, t} = this.tt
    const form = formRef.current
    const schoolNameValue = form?.getValue("invoice_info[name]") ?? this.s.schoolName

    return (
      <View dataSet={this.rootDataSet ||= {component: "plans--sign-up-modal--step-invoice-info"}}>
        <Form formObjectRef={this.tt.formRef}>
          <View style={this.headerWrapStyle ||= {display: "flex", alignItems: "center"}}>
            <Heading>
              {t(".invoice_information")}
            </Heading>
          </View>
          <UtilsInput
            dataSet={this.schoolNameInputDataSet ||= {class: "school-name-input"}}
            defaultValue={schoolNameValue}
            label={t(".school_name")}
            name="invoice_info[name]"
            required
            style={this.inputStyle ||= {marginBottom: 15}}
          />
          {country?.countryCode() != "US" &&
            <UtilsInput
              dataSet={this.vatNumberInputDataSet ||= {class: "vat-number-input"}}
              label={t(".vat_number")}
              name="invoice_info[vatNumber]"
              placeholder={t(".vat_number_placeholder")}
              required={country?.countryCode() != "DK" && country?.countryCode() != "US"}
              style={this.inputStyle}
            />
          }
          <UtilsInput
            dataSet={this.streetNameInputDataSet ||= {class: "street-name-input"}}
            label={t(".street_name")}
            name="invoice_info[streetName]"
            required
            style={this.inputStyle}
          />
          <View style={this.rowStyle ||= {display: "flex", flexDirection: "row", marginBottom: 15}}>
            <View style={this.halfWidthStyle ||= {width: "50%"}}>
              <UtilsInput
                dataSet={this.zipCodeInputDataSet ||= {class: "zip-code-input"}}
                label={t(".zip_code")}
                name="invoice_info[zipCode]"
                required
                style={this.zipCodeInputStyle ||= {marginRight: 5}}
              />
            </View>
            <View style={this.halfWidthStyle}>
              <UtilsInput
                dataSet={this.cityInputDataSet ||= {class: "city-input"}}
                label={t(".city")}
                name="invoice_info[city]"
                required
                style={this.cityInputStyle ||= {marginLeft: 5}}
              />
            </View>
          </View>
          <UtilsInput
            dataSet={this.emailInputDataSet ||= {class: "email-for-invoice-input"}}
            label={t(".email_for_invoice")}
            name="invoice_info[email]"
            required
            style={this.inputStyle}
          />
          <UtilsInput
            dataSet={this.noteInputDataSet ||= {class: "note-or-other-input"}}
            label={t(".note_or_other")}
            name="invoice_info[note]"
            style={this.inputStyle}
          />
          <UtilsButton
            dataSet={this.submitButtonDataSet ||= {class: "submit-invoice-info-button"}}
            onPress={this.tt.onContinuePressed}
            title={t(".continue")}
          />
        </Form>
      </View>
    )
  }

  onContinuePressed = async () => {
    const {country, currentUser, t} = this.tt
    const formData = this.tt.formRef.current.asObject()
    const invoiceInfoForm = formData.invoice_info || {}
    const {city, email, name, note, streetName, vatNumber, zipCode} = invoiceInfoForm

    if (isUserA(currentUser, "tester")) {
      if (vatNumber != "") {
        try {
          await loading(t(".checking_vat_number"), async () => {
            await VatNumber.lookup({vat_number: vatNumber})
          })
        } catch (error) {
          FlashMessage.errorResponse(error)

          return
        }
      }
    }

    const invoiceInfo = {
      name,
      vatNumber: presence(vatNumber),
      streetName,
      zipCode,
      city,
      email,
      note
    }

    const inputValidator = new InputValidator({
      inputs: invoiceInfo,
      translations: {
        city: InvoiceInfo.humanAttributeName("city"),
        email: InvoiceInfo.humanAttributeName("email"),
        name: Contact.humanAttributeName("name"),
        note: InvoiceInfo.humanAttributeName("note"),
        streetName: InvoiceInfo.humanAttributeName("streetName"),
        vatNumber: InvoiceInfo.humanAttributeName("vatNumber"),
        zipCode: InvoiceInfo.humanAttributeName("zipCode")
      }
    })

    inputValidator
      .validate("city", {presence: true})
      .validate("email", {presence: true})
      .validate("name", {presence: true})
      .validate("streetName", {presence: true})
      .validate("zipCode", {presence: true})

    if (country?.countryCode() != "DK" && country?.countryCode() != "US") {
      inputValidator.validate("vatNumber", {presence: true})
    }

    if (inputValidator.isInvalid()) {
      FlashMessage.alert(inputValidator.getErrorMessages().join(" "))
    } else {
      this.p.onInvoiceInfoSubmit({invoiceInfo})
    }
  }

  ensureSchoolDefaultsLoaded = () => {
    const {resource, school} = this.p

    if (!(resource instanceof SchoolClass)) return

    if (!school) return

    this.setState({schoolName: school.name()})
  }
}

export default memo(shapeComponent(ComponentsPlansSignUpModalStepInvoiceInfo))
